"use client";

import React from "react";
import Link from "next/link";
import { getPostImageUrl } from "@/components/ui/PostImage";
import { VideoPlayIndicator } from "@/components/ui/VideoPlayIndicator";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import type { Post } from "@/payload-types";

export interface LatestArticlesSidebarProps {
    articles: Post[];
    /** Section title. Pass `false` to hide. */
    title?: string | false;
    /** Maximum number of articles to show. Defaults to 6. */
    maxItems?: number;
}

/**
 * LatestArticlesSidebar - Compact vertical list of articles for sidebar placement.
 *
 * Renders articles as horizontal cards (small thumbnail left + title right).
 * Used as a companion to the ArticleCarousel hero in the Editorial variant.
 */
export function LatestArticlesSidebar({ articles, title, maxItems = 7 }: LatestArticlesSidebarProps) {
    const nav = useNavigation();
    const items = articles.slice(0, maxItems);

    if (items.length === 0) return null;

    return (
        <div className="flex flex-col gap-3 h-full justify-start">
            {title !== false && title && <h3 className="text-lg font-bold">{title}</h3>}
            {items.map((post) => {
                const imageUrl = getPostImageUrl(post, "thumbnail");
                const isVideo = post.type === "video";

                return (
                    <Link
                        key={post.id}
                        href={nav.post(post)}
                        prefetch={false}
                        className="group flex gap-3 items-center"
                    >
                        {/* Thumbnail */}
                        {imageUrl && (
                            <div className="relative w-20 h-14 rounded-md overflow-hidden shrink-0 bg-muted">
                                <img src={imageUrl} alt={post.title} className="w-full h-full object-cover" />
                                {isVideo && <VideoPlayIndicator size="sm" />}
                            </div>
                        )}

                        {/* Title */}
                        <h3 className="text-sm font-semibold leading-tight line-clamp-3 group-hover:underline">
                            {post.title}
                        </h3>
                    </Link>
                );
            })}
        </div>
    );
}
